import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import cn from "classnames";
import { APP_CONTAINER_CLASSNAME } from "constants/common.constants";
import { AppDOM } from "utils/app.utils";
import { PortalProps, TooltipProps } from "./Tooltip.types";
import styles from "./Tooltip.module.scss";

import {
  autoUpdate,
  computePosition as computePositionBase,
  limitShift,
  offset as computePositionOffset,
  shift,
} from "@floating-ui/dom";

const Portal = ({ children, className }: PortalProps) => {
  const [nodeElement] = useState(document.createElement("div"));

  useEffect(() => {
    const appContainer = AppDOM.querySelector<HTMLDivElement>(`.${APP_CONTAINER_CLASSNAME}`);
    appContainer?.appendChild(nodeElement);
    if (className) {
      nodeElement.className = className;
    }
    return () => nodeElement?.remove();
  }, []);

  return ReactDOM.createPortal(children, nodeElement);
};

export const Tooltip = ({
  triggerElement,
  placement = "top",
  className,
  portalClassName,
  children,
  offsetY,
  offsetX = 0,
  enabledOnMobile = false,
  disabled = false,
}: TooltipProps) => {
  const triggerRef = useRef<null | HTMLDivElement>(null);
  const tooltipRef = useRef<null | HTMLDivElement>(null);
  const [canShowTooltip, setCanShowTooltip] = useState(false);
  const [calculatedPos, setCalculatedPos] = useState({ left: -5000, top: 0 });

  const portalInlineStyles: CSSProperties = {
    transform: `translate(${calculatedPos.left + offsetX}px, ${calculatedPos.top}px)`,
  };

  const updatePosition = () => {
    const middleware = [
      computePositionOffset({ mainAxis: offsetY }),
      shift({
        limiter: limitShift({
          crossAxis: false,
        }),
      }),
    ];
    if (triggerRef.current && tooltipRef.current) {
      computePositionBase(triggerRef.current, tooltipRef.current, {
        placement,
        middleware,
      }).then(({ x, y }) => {
        setCalculatedPos({ top: y, left: x });
      });
    }
  };

  const resetPosition = () => {
    if (triggerRef.current && tooltipRef.current) {
      autoUpdate(triggerRef.current, tooltipRef.current, updatePosition);
    }
  };

  useLayoutEffect(() => {
    updatePosition();
  }, []);

  useEffect(() => {
    if (!canShowTooltip) return;
    updatePosition();
  }, [canShowTooltip]);

  const showTooltipHandler = () => {
    if (disabled) return;
    setCanShowTooltip(true);
  };

  const hideTooltipHandler = () => {
    setCanShowTooltip(false);
    resetPosition();
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={cn(styles.tooltip, className)}
        onMouseEnter={showTooltipHandler}
        onMouseLeave={hideTooltipHandler}
      >
        {triggerElement}
      </div>
      {canShowTooltip && (
        <Portal className={cn(styles.portalWrap, portalClassName, { [styles.enabledOnMobile]: enabledOnMobile })}>
          <div style={portalInlineStyles} ref={tooltipRef}>
            {children}
          </div>
        </Portal>
      )}
    </>
  );
};
